import React from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';

import { ChevronRight } from '@bibliocommons/base-icons';
import { SecondaryTextButton } from '@bibliocommons/base-buttons';
import { availabilityShape, bibShape, branchShape, subscriptionShape } from '@bibliocommons/bc-prop-types';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';

import {
  FullScreenOverlayHeader,
  FullScreenOverlayContent,
  FullScreenOverlayBody,
  FullScreenOverlayContainer
} from 'app/components/shared/FullScreenOverlay';
import AvailabilityDetailsButton from 'app/components/bibs/shared/AvailabilityDetailsButton';
import { BibBriefContainer } from 'app/components/bibs/shared/BibBrief';

import StackMapButton from 'app/components/bibs/shared/StackMap/StackMapButton';
import StackMapDisplay from 'app/components/bibs/shared/StackMap/StackMapDisplay';
import { MATERIAL_TYPE_PHYSICAL } from 'app/constants/BibConstants';
import bibItemShape from 'app/shapes/bibItemShape';
import stackMapStateShape from 'app/shapes/stackMapStateShape';
import BibItemField from '../BibItemField';

import './BranchAvailabilityInfo.scss';

function isAvailableAtLocation(item, branch) {
  return (
    item.getIn(['availability', 'statusType']) === 'AVAILABLE' && item.getIn(['branch', 'code']) === branch.get('code')
  );
}

const DetailsButtonOverride = props => {
  return <SecondaryTextButton className="details-button" renderTrailingIcon={() => <ChevronRight />} {...props} />;
};

export default class BranchAvailabilityInfo extends React.PureComponent {
  state = { isStackMapOpen: false };

  handleStackMapOpen = (domain, data) => {
    const { fetchStackMap } = this.props;

    this.setState({ isStackMapOpen: true });

    return fetchStackMap(domain, data);
  };

  handleStackMapClose = () => {
    this.setState({ isStackMapOpen: false });
  };

  renderBranchInfo() {
    const { realtimeAvailability, bibItems, preferredBranches, stackMapState } = this.props;

    if (realtimeAvailability.get('statusType') === 'AVAILABLE') {
      // Display branch details based on the order of the user's preferred locations
      for (let idx = 0; idx < preferredBranches.size; idx++) {
        const branch = preferredBranches.get(idx);
        const availableItem = bibItems.find(item => isAvailableAtLocation(item, branch));

        if (availableItem) {
          return (
            <div className="branch-info">
              <div className="branch-location">
                <FormattedMessage
                  id="find_at_branch_location"
                  values={{ branch: availableItem.getIn(['branch', 'name']) }}
                />
              </div>
              <BibItemField field={<FormattedMessage id="collection" />} value={availableItem.get('collection')} />
              <BibItemField field={<FormattedMessage id="bib_call_number" />} value={availableItem.get('callNumber')} />
              <StackMapButton
                bibItem={availableItem}
                fetchStackMap={this.handleStackMapOpen}
                stackMapState={stackMapState}
              />
            </div>
          );
        }
      }
    }

    return null;
  }

  renderStackMapOverlay() {
    const { stackMapState } = this.props;
    const { isStackMapOpen } = this.state;

    return (
      <FullScreenOverlayContainer
        open={isStackMapOpen}
        handleClose={this.handleStackMapClose}
        overlayKey="stack-map-overlay"
        dataKey="stack-map-overlay"
      >
        <FullScreenOverlayBody>
          <FullScreenOverlayHeader title={<FormattedMessage id="find_item" />} />
          <FullScreenOverlayContent size="medium">
            <BibBriefContainer metadataId={this.props.bib.get('id')} />
            {isStackMapOpen && <StackMapDisplay stackMapState={stackMapState} />}
          </FullScreenOverlayContent>
        </FullScreenOverlayBody>
      </FullScreenOverlayContainer>
    );
  }

  render() {
    const { bib, bibItems, handleAvailabilityButtonClick, realtimeAvailability, subscriptions } = this.props;

    const materialType = bib.getIn(['policy', 'materialType']);

    if (materialType === MATERIAL_TYPE_PHYSICAL && (bibItems.size > 0 || subscriptions.size > 0)) {
      return (
        <div className="cp-branch-availability-info">
          {this.renderBranchInfo()}
          {this.renderStackMapOverlay()}
          <AvailabilityDetailsButton
            bib={bib}
            availability={realtimeAvailability}
            ButtonComponent={DetailsButtonOverride}
            handleButtonClick={handleAvailabilityButtonClick}
          >
            <span aria-hidden>
              <FormattedMessage id="availability_by_location" />
            </span>
            <ScreenReaderMessage>
              <FormattedMessage id="sr_availability_by_location" />
            </ScreenReaderMessage>
          </AvailabilityDetailsButton>
        </div>
      );
    }

    return null;
  }
}

BranchAvailabilityInfo.propTypes = {
  bib: bibShape.isRequired,
  bibItems: ImmutablePropTypes.listOf(bibItemShape).isRequired,
  fetchStackMap: PropTypes.func.isRequired,
  handleAvailabilityButtonClick: PropTypes.func,
  preferredBranches: ImmutablePropTypes.listOf(branchShape).isRequired,
  realtimeAvailability: availabilityShape.isRequired,
  stackMapState: stackMapStateShape.isRequired,
  subscriptions: ImmutablePropTypes.listOf(subscriptionShape).isRequired
};
